import { createRouter, createWebHistory } from 'vue-router';
import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router';
import { authService } from '@/plugins/vue-auth.ts';
import { ls } from '@/plugins/vue-ls.ts';
import AuthErrors from '@/constants/AuthErrors.ts';
import NewRelicBrowser from '@/plugins/newrelic-browser.ts';
import { isChunkLoadError, reloadForStaleChunk } from '@/plugins/chunk-reload.ts';
import AuthUser from '@/entities/AuthUser.js';
import { resetLeadPageTiming, startLeadPageNavigation } from '@/telemetry/leadPageTiming.ts';
import { observeStartupRoute, markStartupPhase } from '@/telemetry/startupTiming.ts';
import {
    observeAssetFailure,
    startAssetRecoveryNavigation,
    cancelAssetRecoveryNavigation,
    resolveAssetRecoveryNavigation,
} from '@/telemetry/assetRecovery.ts';
import { isSm } from '../utils/layout.ts';

import accounts from './accounts.js';
import auth from './auth.ts';
import automations from './automations.js';
import campaign from './campaign.js';
import calendar from './calendar.js';
import consent from './consent.js';
import dashboards from './dashboards.js';
import duplicates from './duplicates.js';
import flow from './flow.js';
import groups from './groups.js';
import homePage from './homePage.js';
import imports from './imports.js';
import inventory from './inventory.js';
import analytics from './analytics.js';
import inTurn from './inTurn.js';
import leads from './leads.js';
import leadTrash from './leadTrash.js';
import redirects from './redirects.js';
import schedule from './schedule.js';
import search from './search.js';
import suppliers from './suppliers.js';
import templates from './templates.js';
import users from './users.js';
import unsubscribe from './unsubscribe.js';
import customers from './customers.ts';
import sms from './sms.js';

import NotFound from '../views/NotFound.vue';
import Unauthorized from '../views/Unauthorized.vue';
import Forbidden from '../views/Forbidden.vue';

if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
}

const processEnv =
    (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env ?? {};

const routes: RouteRecordRaw[] = [
    ...auth,
    ...redirects,
    {
        path: '/unauthorized',
        name: 'unauthorized',
        component: Unauthorized,
        meta: {
            header: {
                account: true,
                notification: true,
                search: true,
                settings: true,
            },
            layout: {
                contentHeader: false,
            },
        },
    },
    {
        path: '/forbidden',
        name: 'forbidden',
        component: Forbidden,
        props: (route) => ({ error: route.query.error }),
        meta: {
            splashscreen: true,
        },
    },
    ...suppliers,
    ...accounts,
    ...automations,
    ...campaign,
    ...calendar,
    ...consent,
    ...dashboards,
    ...duplicates,
    ...flow,
    ...groups,
    ...homePage,
    ...imports,
    ...inventory,
    ...analytics,
    ...inTurn,
    ...leads,
    ...schedule,
    ...search,
    ...templates,
    ...users,
    ...unsubscribe,
    ...customers,
    ...leadTrash,
    ...sms,
    {
        path: '/:catchAll(.*)*',
        name: 'notFound',
        component: NotFound,
        meta: {
            header: {
                account: true,
                notification: true,
                search: true,
                settings: true,
            },
            layout: {
                contentHeader: false,
            },
        },
    },
];

if (processEnv.NODE_ENV !== 'production' || processEnv.VUE_APP_SHOW_SANDBOX === 'true') {
    routes.push(
        {
            name: 'sandbox',
            path: '/sandbox',
            component: () => import('../sandbox/Sandbox.vue'),
            meta: {
                landing: true,
                header: {
                    search: true,
                },
            },
        },
        {
            path: '/sandbox/:version',
            component: () => import('../sandbox/Version.vue'),
            meta: {
                landing: true,
                header: {
                    search: true,
                },
            },
        }
    );
}

const routesPhoneDisabled = [
    'templates.',
    'automations.',
    'schedule',
    'flow.',
    'duplicates',
    'imports.',
    'users.',
    'accounts.',
    'groups.',
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (to.name == from.name) {
            return false;
        }

        if (savedPosition) {
            return savedPosition;
        }

        return { left: 0, top: 0 };
    },
});

router.afterEach((to, _from, failure) => {
    if (failure) {
        cancelAssetRecoveryNavigation(to);
    } else {
        resolveAssetRecoveryNavigation(to);
    }
});

// When a lazy route component fails to load because its chunk was purged by a new
// deploy, reload once so the tab picks up the fresh asset manifest. Loop-guarded in
// reloadForStaleChunk(). See src/plugins/chunk-reload.ts.
router.onError((error, to) => {
    if (isChunkLoadError(error)) {
        observeAssetFailure(error, 'router');
        reloadForStaleChunk();
    }
    cancelAssetRecoveryNavigation(to);
});

router.isReady().then(() => {
    // TODO: this is a huge hack and should be removed! Basically, there is a `ListenToBroadcasting` file that defines plain html snippets of buttons that should navigate to specific pages when clicked. Here, a global event listener checks EVERY CLICK EVENT ON THE PAGE and if it finds a button with a `data-vue-router` attribute, it will navigate to the route specified in the attribute. This completely skips the Vue framework and its principles...
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const route = target.getAttribute('data-vue-router');

        if (!route) {
            return;
        }

        router.push(route);
    });
});

// Auth flow notes:
// - Initial session validation (token expiry, refresh) is handled by authService.handshake() in main.js.
// - When API calls return 401 (expired session), the axios response interceptor in plugins/axios.js
//   attempts a silent token refresh via handleRefreshToken(). If that fails, it redirects to the
//   Auth0 login page. This is the primary mechanism for redirecting logged-out users to login.
const checkCrmLiteAccess = async (
    to: RouteLocationNormalized
): Promise<RouteLocationRaw | undefined> => {
    if (
        !authService.check() ||
        to.meta.landing === true ||
        to.name === 'forbidden' ||
        to.path.startsWith('/auth/') ||
        to.path === '/new-password'
    ) {
        return undefined;
    }

    try {
        if (!(await authService.waitForStartupAuthentication())) {
            return { name: 'forbidden', query: { error: 'crmLiteFullCrmForbidden' } };
        }
    } catch (error) {
        console.error('Unable to check CRM Lite access:', error);
        return {
            name: 'auth.error',
            query: {
                error: AuthErrors.INITIALIZATION_FAILED,
                target: to.fullPath,
            },
        };
    }
    return undefined;
};

const getPageRedirect = (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized
): RouteLocationRaw | false | undefined => {
    const routeName = typeof to.name === 'string' ? to.name : null;

    if (isSm && routeName && routesPhoneDisabled.some((v) => routeName.includes(v))) {
        return '/';
    }

    if (to.hash) {
        return {
            path: to.path,
            query: to.query,
            hash: '',
        };
    }

    if (
        window.reloadNextPage &&
        typeof to.name === 'string' &&
        typeof from.name === 'string' &&
        to.name !== from.name
    ) {
        window.location.assign(to.fullPath);
        return false;
    }

    return undefined;
};

const getDefaultDashboard = async (): Promise<RouteLocationRaw | true> => {
    try {
        const user = (await authService.waitForUser(true)) as { default_dashboard?: string };
        let defaultPath = 'dashboards.allLead';

        if (user.default_dashboard && new AuthUser(user).hasAccessTo(user.default_dashboard)) {
            defaultPath = user.default_dashboard;
        }

        const resolved = router.resolve({ name: defaultPath });

        if (resolved.matched.length > 0) {
            return { name: defaultPath };
        }

        return { name: 'dashboards.allLead' };
    } catch (e) {
        console.error('Error while redirecting to default dashboard:', e);

        if (router.currentRoute.value?.name === 'forbidden') {
            return true;
        }

        return { name: 'dashboards.allLead' };
    }
};

router.beforeEach(async (to, from) => {
    observeStartupRoute(to);
    startAssetRecoveryNavigation(to, from);

    // Auth callbacks are handled in main.js before the app mounts.
    if (to.path === '/auth/callback' || to.path === '/auth/mfa-callback') {
        return true;
    }

    // Public pages skip App's CRM bootstrap and bearer-token setup. Reload before
    // checking admission so the protected page starts with its stored token.
    const cameFromPublicRoute =
        (from.meta?.landing === true ||
            from.meta?.splashscreen === true ||
            from.meta?.auth === false) &&
        from.name !== 'auth.callback' &&
        from.name !== 'auth.mfaCallback';
    const goesToProtectedRoute =
        to.meta.landing !== true && to.meta.splashscreen !== true && to.meta.auth !== false;
    if (cameFromPublicRoute && goesToProtectedRoute) {
        globalThis.location.assign(to.fullPath);
        return false;
    }

    const accessRedirect = await checkCrmLiteAccess(to);
    if (accessRedirect !== undefined) {
        return accessRedirect;
    }

    if (to.query.ref) {
        ls.set('referer', to.query.ref);
    }

    const pageRedirect = getPageRedirect(to, from);
    if (pageRedirect !== undefined) {
        return pageRedirect;
    }

    if (to.path === '/') {
        return getDefaultDashboard();
    }

    if (to.name === 'leads.update') {
        startLeadPageNavigation(to.params?.id as string);
    } else {
        resetLeadPageTiming();
    }

    return true;
});

router.beforeEach((to) => {
    const routeName = to.name?.toString() || to.path;
    NewRelicBrowser.setPageViewName(routeName);
    NewRelicBrowser.setCurrentRouteName(routeName);
    return true;
});

router.afterEach((_to, _from, failure) => {
    if (!failure) markStartupPhase('route_resolved');
});

export default router;
